import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { AlertTriangle } from 'lucide-react';
import { routeTree } from './routes/__root';
import { setTokenGetter, api } from './lib/api';
import { SetupPage } from './pages/SetupPage';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './components/ui/card';
import { Button } from './components/ui/button';
import './i18n';
import './index.css';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
    ],
    tracesSampleRate: 0.2,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

// Create router
const router = createRouter({ routeTree });

// Register router for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Detect dev mode: Auth0 domain is a placeholder or not configured
const auth0Domain = import.meta.env.VITE_AUTH0_DOMAIN ?? '';
const auth0ClientId = import.meta.env.VITE_AUTH0_CLIENT_ID ?? '';
const isDevAuth = !auth0Domain || auth0Domain.includes('placeholder') || !auth0ClientId || auth0ClientId === 'dev-placeholder';

type AuthStatus = 'loading' | 'active' | 'needs_setup' | 'pending_claim' | 'inactive' | 'error';

interface AuthStatusResponse {
  status: 'active' | 'needs_setup' | 'pending_claim' | 'inactive';
  email?: string;
}

function BrandLogo() {
  return (
    <img src="/logo-192.png" alt="Taxistry" className="w-12 h-12 mx-auto rounded-lg" />
  );
}

function LoadingScreen() {
  return (
    <div className="flex items-center justify-center h-screen bg-background">
      <div className="text-center space-y-3">
        <BrandLogo />
        <p className="text-sm text-muted-foreground">Φόρτωση...</p>
      </div>
    </div>
  );
}

function ErrorScreen({ title, description, action }: {
  title: string;
  description: string;
  action?: { label: string; onClick: () => void };
}) {
  return (
    <div className="flex items-center justify-center h-screen bg-background p-6">
      <Card className="max-w-sm w-full text-center">
        <CardHeader>
          <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertTriangle className="h-6 w-6 text-destructive" />
          </div>
          <CardTitle className="text-lg">{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        {action ? (
          <CardContent>
            <Button variant="outline" className="w-full" onClick={action.onClick}>
              {action.label}
            </Button>
          </CardContent>
        ) : null}
      </Card>
    </div>
  );
}

function AuthenticatedApp() {
  const { getAccessTokenSilently, isAuthenticated, isLoading, loginWithRedirect, error: auth0Error } = useAuth0();
  const [authStatus, setAuthStatus] = React.useState<AuthStatus>('loading');
  const [setupEmail, setSetupEmail] = React.useState<string | undefined>();

  // Wire Auth0 token getter into the API client
  React.useEffect(() => {
    if (isAuthenticated) {
      setTokenGetter(() =>
        getAccessTokenSilently({
          authorizationParams: {
            audience: import.meta.env.VITE_AUTH0_AUDIENCE ?? '',
          },
        }),
      );
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  // Check auth status after authentication
  React.useEffect(() => {
    if (!isAuthenticated) return;

    let cancelled = false;
    async function checkStatus() {
      try {
        const res = await api.get<AuthStatusResponse>('/auth/status');
        if (cancelled) return;
        setAuthStatus(res.status);
        if (res.status === 'needs_setup') {
          setSetupEmail(res.email);
        }
      } catch {
        if (!cancelled) setAuthStatus('error');
      }
    }
    checkStatus();
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  // Show loading while Auth0 initializes or processes callback
  if (isLoading) {
    return <LoadingScreen />;
  }

  // Auth0 SDK error (e.g. audience mismatch, consent required)
  if (auth0Error) {
    return (
      <ErrorScreen
        title="Σφάλμα Σύνδεσης"
        description={auth0Error.message}
        action={{ label: 'Δοκιμάστε ξανά', onClick: () => { window.location.href = window.location.origin; } }}
      />
    );
  }

  // Redirect to Auth0 login if not authenticated
  // Guard: don't redirect if we're in the middle of an Auth0 callback
  if (!isAuthenticated) {
    const params = new URLSearchParams(window.location.search);
    if (params.has('code') && params.has('state')) {
      // Auth0 callback in progress — wait for SDK to process
      return <LoadingScreen />;
    }
    loginWithRedirect();
    return null;
  }

  // Auth status state machine
  if (authStatus === 'loading') {
    return <LoadingScreen />;
  }

  if (authStatus === 'needs_setup') {
    return (
      <SetupPage
        email={setupEmail}
        onComplete={() => setAuthStatus('active')}
      />
    );
  }

  if (authStatus === 'inactive') {
    return (
      <ErrorScreen
        title="Λογαριασμός Ανενεργός"
        description="Ο λογαριασμός σας έχει απενεργοποιηθεί. Επικοινωνήστε με τον διαχειριστή του γραφείου σας."
      />
    );
  }

  if (authStatus === 'error') {
    return (
      <ErrorScreen
        title="Σφάλμα Σύνδεσης"
        description="Δεν ήταν δυνατός ο έλεγχος του λογαριασμού σας."
        action={{ label: 'Δοκιμάστε ξανά', onClick: () => window.location.reload() }}
      />
    );
  }

  // active or pending_claim → render app
  return <RouterProvider router={router} />;
}

function DevApp() {
  // In dev mode, skip Auth0 entirely — API uses dev bypass
  return <RouterProvider router={router} />;
}

function Root() {
  if (isDevAuth) {
    return (
      <QueryClientProvider client={queryClient}>
        <DevApp />
      </QueryClientProvider>
    );
  }

  return (
    <Auth0Provider
      domain={auth0Domain}
      clientId={auth0ClientId}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: import.meta.env.VITE_AUTH0_AUDIENCE ?? '',
      }}
    >
      <QueryClientProvider client={queryClient}>
        <AuthenticatedApp />
      </QueryClientProvider>
    </Auth0Provider>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={
      <ErrorScreen
        title="Κάτι πήγε στραβά"
        description="Παρουσιάστηκε ένα απρόσμενο σφάλμα."
        action={{ label: 'Επαναφόρτωση', onClick: () => window.location.reload() }}
      />
    }>
      <Root />
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);
